import { Link } from "react-router-dom";
import { Heart, Gauge, Fuel, Settings2, Calendar, MapPin, BadgeCheck } from "lucide-react";
import { useShortlist } from "@/context/ShortlistContext";
import { formatPriceLakh, formatKm, vehicleTitle } from "@/lib/format";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const toAbsolute = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

export const VehicleCard = ({ vehicle, compact = false }) => {
  const { has, toggle } = useShortlist();
  const isSold = vehicle.status === "sold";
  const imgRaw = vehicle.images?.[vehicle.primary_image_idx || 0] || vehicle.images?.[0] || "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
  const img = toAbsolute(imgRaw);

  return (
    <Link to={`/car/${vehicle.id}`} className={`vehicle-card group block ${isSold ? "opacity-80" : ""}`} data-testid={`vehicle-card-${vehicle.id}`}>
      <div className="relative aspect-[16/10] overflow-hidden bg-gray-100">
        <img
          src={img}
          alt={vehicleTitle(vehicle)}
          loading="lazy"
          className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${isSold ? "grayscale" : ""}`}
        />
        <div className="absolute top-3 left-3 flex gap-1.5 flex-wrap">
          {vehicle.cs_certified && (
            <span className="inline-flex items-center gap-1 badge-red text-[10px] font-bold px-2 py-1 rounded-md uppercase tracking-wider">
              <BadgeCheck size={12} /> CS Certified
            </span>
          )}
          {vehicle.owners === 1 && !isSold && (
            <span className="bg-white/95 text-gray-800 text-[10px] font-bold px-2 py-1 rounded-md uppercase tracking-wider border border-gray-200">1st Owner</span>
          )}
          {isSold && <span className="bg-[#777] text-white text-[10px] font-bold px-2 py-1 rounded-md uppercase tracking-wider">Sold</span>}
          {vehicle.status === "reserved" && <span className="bg-[#F59E0B] text-white text-[10px] font-bold px-2 py-1 rounded-md uppercase tracking-wider">Reserved</span>}
        </div>
        <button
          onClick={(e) => { e.preventDefault(); toggle(vehicle.id); }}
          className="absolute top-3 right-3 h-8 w-8 rounded-full bg-white/95 flex items-center justify-center shadow-sm hover:scale-105 transition-transform"
          aria-label="Shortlist"
          data-testid={`shortlist-btn-${vehicle.id}`}
        >
          <Heart size={16} className={has(vehicle.id) ? "fill-[#940C2C] text-[#940C2C]" : "text-gray-700"} />
        </button>
      </div>

      <div className="p-4">
        <h3 className="font-display font-bold text-base text-gray-900 leading-snug line-clamp-1" data-testid={`vehicle-title-${vehicle.id}`}>{vehicleTitle(vehicle)}</h3>
        <div className="mt-2 flex items-center gap-3 flex-wrap text-xs text-gray-600">
          <span className="inline-flex items-center gap-1"><Gauge size={12} />{formatKm(vehicle.km_driven)}</span>
          <span className="inline-flex items-center gap-1"><Fuel size={12} />{vehicle.fuel}</span>
          <span className="inline-flex items-center gap-1"><Settings2 size={12} />{vehicle.transmission}</span>
          <span className="inline-flex items-center gap-1"><Calendar size={12} />{vehicle.year}</span>
        </div>
        {!compact && (
          <div className="mt-2 text-xs text-gray-500 inline-flex items-center gap-1"><MapPin size={11} />{vehicle.location || "Vikaspuri, Delhi"}</div>
        )}
        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
          <span className="font-display font-extrabold text-lg text-[#940C2C]" data-testid={`vehicle-price-${vehicle.id}`}>{formatPriceLakh(vehicle.price)}</span>
          <span className="text-xs font-semibold text-gray-700 group-hover:text-[#940C2C]">View Car →</span>
        </div>
      </div>
    </Link>
  );
};
